import React, { useEffect, useState } from "react";
import "./ProfileMenu.css";
import { useNavigate } from "react-router-dom";
import LocationPicker from "../../common/LocationPicker";

import {
  User,
  Bell,
  MapPin,
  Star,
  Building2,
  CircleHelp,
  MessageCircle,
  LifeBuoy,
  Settings,
  LogOut,
  ChevronRight,
  X,
} from "lucide-react";

export default function ProfileMenu({
  open,
  onClose,
  user = {},
  onLogout = () => {},
}) {
  const navigate = useNavigate();

  const [showLocation, setShowLocation] = useState(false);

  const [location, setLocation] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("civilink_location"));
    } catch {
      return null;
    }
  });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  if (!open) return null;

  const safeUser =
    user && Object.keys(user).length ? user : {};

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <div
      className="profile-drawer-backdrop"
      onClick={onClose}
    >
      <div
        className="profile-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* HEADER */}
        <div className="profile-menu-top">
          <button
            className="profile-menu-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* PROFILE */}
        <div className="profile-menu-header">
          <div className="profile-menu-avatar">
            {safeUser.profilePhoto ? (
              <img
                src={safeUser.profilePhoto}
                alt={safeUser.name || "User"}
                className="profile-menu-avatar-img"
              />
            ) : safeUser.avatar ? (
              <img
                src={safeUser.avatar}
                alt={safeUser.name || "User"}
                className="profile-menu-avatar-img"
              />
            ) : (
              safeUser?.name?.charAt(0)?.toUpperCase() || "U"
            )}
          </div>

          <div className="profile-menu-info">
            <h3>
              {safeUser?.name || "Civilink User"}
            </h3>

            <p>
              {safeUser?.profession || "General Member"}
            </p>
          </div>
        </div>

        <div className="drawer-sep" />

        <div className="profile-drawer-body">

          {/* ACCOUNT */}
          <div className="drawer-section-title">
            ACCOUNT
          </div>

          <button
            className="drawer-item"
            onClick={() => handleNavigate("/profile")}
          >
            <span className="drawer-item-icon">
              <User size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>View Profile</strong>
              <small>
                View and manage your profile
              </small>
            </span>

            <ChevronRight size={17} className="drawer-arrow" />
          </button>

          <button
            className="drawer-item"
            onClick={() =>
              handleNavigate("/notifications")
            }
          >
            <span className="drawer-item-icon">
              <Bell size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>Notifications</strong>
              <small>
                View your latest updates
              </small>
            </span>

            <ChevronRight size={17} className="drawer-arrow" />
          </button>

          <button
            className="drawer-item"
            onClick={() => setShowLocation(true)}
          >
            <span className="drawer-item-icon">
              <MapPin size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>Location</strong>

              <small>
                {location
                  ? `${location.city}${
                      location.state
                        ? ", " + location.state
                        : ""
                    }`
                  : "Set your location"}
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          <button
            className="drawer-item"
            onClick={() => handleNavigate("/saved")}
          >
            <span className="drawer-item-icon">
              <Star size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>Saved</strong>
              <small>
                View your saved posts and properties
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          <div className="drawer-sep" />

          {/* CIVILINK */}
          <div className="drawer-section-title">
            CIVILINK
          </div>

          <button
            className="drawer-item"
            onClick={() => handleNavigate("/about")}
          >
            <span className="drawer-item-icon">
              <Building2 size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>About Civilink</strong>
              <small>
                Learn about the Civilink platform
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          <div className="drawer-sep" />

          {/* HELP */}
          <div className="drawer-section-title">
            HELP & SUPPORT
          </div>

          <button
            className="drawer-item"
            onClick={() => handleNavigate("/help")}
          >
            <span className="drawer-item-icon">
              <CircleHelp size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>How Civilink Works</strong>
              <small>
                Learn how to use Civilink
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          <button
            className="drawer-item"
            onClick={() =>
              handleNavigate("/contact")
            }
          >
            <span className="drawer-item-icon">
              <MessageCircle size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>Contact Us</strong>
              <small>
                Get in touch with Civilink
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          <button
            className="drawer-item"
            onClick={() =>
              handleNavigate("/support")
            }
          >
            <span className="drawer-item-icon">
              <LifeBuoy size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>Help & Support</strong>
              <small>
                Get help with a problem
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          <div className="drawer-sep" />

          {/* OTHER */}
          <div className="drawer-section-title">
            OTHER
          </div>

          <button
            className="drawer-item"
            onClick={() =>
              handleNavigate("/settings")
            }
          >
            <span className="drawer-item-icon">
              <Settings size={19} />
            </span>

            <span className="drawer-item-content">
              <strong>Settings</strong>
              <small>
                Manage your preferences
              </small>
            </span>

            <ChevronRight
              size={17}
              className="drawer-arrow"
            />
          </button>

          {/* LOGOUT */}
          <button
            className="drawer-logout"
            onClick={onLogout}
          >
            <span className="drawer-logout-icon">
              <LogOut size={19} />
            </span>

            <span>Logout</span>
          </button>

        </div>

        {/* FOOTER */}
        <div className="profile-menu-footer">
          <div className="civilink-footer-brand">
            <span className="civilink-footer-dot" />
            <strong>CIVILINK</strong>
          </div>

          <span>
            Construction • Professionals • Property
          </span>
        </div>
      </div>

      {/* LOCATION MODAL */}
     {showLocation && (
  <LocationPicker
    open={showLocation}
    onClose={() => setShowLocation(false)}
    initialLocation={location}
    autoLocate={false}
    onSelect={(loc) => {
      const savedLocation = {
        ...loc,
        latitude: loc.latitude,
        longitude: loc.longitude,
      };

      localStorage.setItem(
        "civilink_location",
        JSON.stringify(savedLocation)
      );

      setLocation(savedLocation);
      setShowLocation(false);
    }}
  />
)}
    </div>
  );
}